import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation } from '@tanstack/react-query';
import type { SubmitReferralInput } from '@/graphql/generated/graphql';
import { requestSubmitReferral, SubmitReferralError } from './referAFriendApi';

/**
 * Sends a Refer a Friend submission. One-shot action — no query key, nothing to cache or
 * invalidate. The modal that renders this data owns `data`/`error` display; this hook only
 * submits and tracks failures.
 */
export function useSubmitReferral() {
  const mutation = useMutation({
    mutationFn: requestSubmitReferral,
    onError: (error: unknown) => {
      const code = error instanceof SubmitReferralError ? error.code : extractGqlErrorCode(error);
      // `trackException` forwards `.message` to Application Insights verbatim — a raw `ClientError`
      // embeds this mutation's own PII-bearing input via graphql-request's `JSON.stringify`, so only
      // a sanitized error ever reaches telemetry.
      trackException(new Error(`submitReferral failed: ${code}`), { source: 'submitReferral', errorCode: code });
    },
  });

  return {
    submit: (input: SubmitReferralInput) => mutation.mutate(input),
    isPending: mutation.isPending,
    data: mutation.data,
    error: mutation.error,
  };
}
